// client/src/components/pages/LexiStar/Routes/RoutesLanes.jsx
//
// LexiStar Routes & lanes (ST-00188 M2-A1; LS-09 §5.11; LS-03 FR-ROUT-001…006).
// Settings > Monitoring > LexiStar Routes & Lanes, route
// /settings/modules/monitoring/lexistar-routes.
//
// Lane list → the lane's route cards (primary, alternates, proposed, retired)
// → the route editor (map + Edit / Versions / Advisories). A route is visible
// when your organisation owns it or either end of its lane
// (get_user_visible_routes_lexistar); only the owner changes it.
//
// Permissions: page lexistar.routes.view (pagePermissions.js + ProtectedRoute,
// re-checked here); lanes, routes, versions lexistar.routes.manage; advisories
// lexistar.routes.manage or lexistar.deviations.approve. The selected lane and
// route live in the URL (?lane=&route=) so a view can be shared.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { FaRoute, FaPlus, FaExclamationTriangle, FaTimes, FaArrowRight } from 'react-icons/fa';
import PageHeader from '../../../common/PageHeader';
import { useAuth } from '../../../../context/AuthContext';
import {
  listLanes,
  createLane,
  listRoutes,
  listSites,
  listGeofences,
  listTollPlazas,
} from '../../../../services/lexistarRoutes';
import LaneList, { NewLaneDialog } from './LaneList';
import RouteCards from './RouteCards';
import RouteEditor from './RouteEditor';

const errorText = (err, fallback) =>
  err?.response?.status === 503
    ? 'LexiStar routes are not installed on this server yet.'
    : err?.response?.data?.error || fallback;

export default function RoutesLanes() {
  const { hasPermission } = useAuth();
  const canView = hasPermission('lexistar.routes.view');
  const canManage = hasPermission('lexistar.routes.manage');
  const canAdvise = hasPermission('lexistar.deviations.approve');

  const [params, setParams] = useSearchParams();
  const laneId = params.get('lane');
  const routeParam = params.get('route'); // uuid | 'new' | null

  const [lanes, setLanes] = useState([]);
  const [lanesLoading, setLanesLoading] = useState(true);
  const [routes, setRoutes] = useState([]);
  const [routesLoading, setRoutesLoading] = useState(false);
  const [sites, setSites] = useState([]);
  const [geofences, setGeofences] = useState([]);
  const [tollPlazas, setTollPlazas] = useState([]);
  const [newLaneOpen, setNewLaneOpen] = useState(false);
  const [error, setError] = useState(null);

  const select = useCallback(
    (next) => {
      const p = new URLSearchParams(params);
      for (const [k, v] of Object.entries(next)) {
        if (v == null) p.delete(k);
        else p.set(k, v);
      }
      setParams(p, { replace: false });
    },
    [params, setParams]
  );

  const fetchLanes = useCallback(async () => {
    if (!canView) return;
    setLanesLoading(true);
    try {
      const res = await listLanes();
      setLanes(res.lanes || []);
    } catch (err) {
      setError(errorText(err, 'Failed to load lanes.'));
    } finally {
      setLanesLoading(false);
    }
  }, [canView]);

  const fetchRoutes = useCallback(async () => {
    if (!canView || !laneId) {
      setRoutes([]);
      return;
    }
    setRoutesLoading(true);
    try {
      const res = await listRoutes({ lane_id: laneId, status: 'all' });
      setRoutes(res.routes || []);
    } catch (err) {
      setError(errorText(err, 'Failed to load routes.'));
    } finally {
      setRoutesLoading(false);
    }
  }, [canView, laneId]);

  useEffect(() => {
    fetchLanes();
  }, [fetchLanes]);

  useEffect(() => {
    fetchRoutes();
  }, [fetchRoutes]);

  // Reference data for the editor; each failure only degrades the editor.
  useEffect(() => {
    if (!canView) return;
    listSites()
      .then(setSites)
      .catch(() => setSites([]));
    listGeofences()
      .then(setGeofences)
      .catch(() => setGeofences([]));
    listTollPlazas()
      .then((r) => setTollPlazas(r.toll_plazas || []))
      .catch(() => setTollPlazas([]));
  }, [canView]);

  const lane = useMemo(() => lanes.find((l) => l.id === laneId) || null, [lanes, laneId]);
  const laneCanChange = lane && (lane.access_type === 'Owner' || lane.access_type === 'Admin');

  // Open the primary route when a lane is picked without a route.
  useEffect(() => {
    if (!lane || routeParam || routesLoading || !routes.length) return;
    const primary = routes.find((r) => r.is_primary) || routes[0];
    select({ route: primary.id });
  }, [lane, routeParam, routes, routesLoading, select]);

  const handleCreateLane = async (data) => {
    const res = await createLane(data);
    setNewLaneOpen(false);
    await fetchLanes();
    select({ lane: res.lane.id, route: 'new' });
  };

  const header = (
    <PageHeader
      icon={FaRoute}
      title="Routes & Lanes"
      subtitle="The ways trucks may drive each lane, with their corridors. Every change is a new version; dispatched trips keep theirs."
      actions={
        canView && canManage ? (
          <button
            type="button"
            onClick={() => setNewLaneOpen(true)}
            className="inline-flex items-center gap-2 px-4 py-2 bg-primary-500 text-white text-sm font-semibold rounded-lg hover:bg-primary-600 shadow-sm"
          >
            <FaPlus size={12} aria-hidden="true" /> New lane
          </button>
        ) : null
      }
    />
  );

  if (!canView) {
    return (
      <div className="min-h-screen bg-slate-50">
        {header}
        <div className="max-w-7xl mx-auto px-3 sm:px-6 py-6">
          <div className="bg-amber-50 border border-amber-200 text-amber-800 px-4 py-3 rounded-lg flex items-start">
            <FaExclamationTriangle className="mr-3 mt-0.5 flex-shrink-0" aria-hidden="true" />
            <p className="font-medium">You do not have permission to view LexiStar routes.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {header}
      <div className="max-w-[1800px] mx-auto px-3 sm:px-6 lg:px-8 py-6">
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6 flex items-start">
            <FaExclamationTriangle className="mr-3 mt-0.5 flex-shrink-0" aria-hidden="true" />
            <p className="flex-1 font-medium">{error}</p>
            <button
              type="button"
              onClick={() => setError(null)}
              aria-label="Dismiss error"
              className="ml-4 text-red-400 hover:text-red-600"
            >
              <FaTimes />
            </button>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-[18rem_minmax(0,1fr)] gap-6 items-start">
          <LaneList
            lanes={lanes}
            loading={lanesLoading}
            selectedId={laneId}
            onSelect={(l) => select({ lane: l.id, route: null })}
          />

          <div className="space-y-4 min-w-0">
            {!lane ? (
              <div className="bg-surface rounded-xl border border-slate-200 p-10 text-center">
                <FaRoute className="mx-auto text-slate-300 mb-3" size={28} aria-hidden="true" />
                <h2 className="text-lg font-medium text-slate-900 mb-1">Choose a lane</h2>
                <p className="text-sm text-slate-600 max-w-md mx-auto">
                  Pick a lane on the left to see its routes, or create one: a lane is a direction
                  between two sites, and its approved routes are the corridors trucks may use.
                </p>
              </div>
            ) : (
              <>
                <div className="flex flex-wrap items-center gap-2">
                  <h2 className="flex items-center gap-2 text-base font-semibold text-slate-900">
                    {lane.from_location_name || lane.from_location_id}
                    <FaArrowRight className="text-slate-400" size={12} aria-hidden="true" />
                    {lane.to_location_name || lane.to_location_id}
                  </h2>
                  {lane.name &&
                    lane.name !== `${lane.from_location_name} → ${lane.to_location_name}` && (
                      <span className="text-sm text-slate-500">{lane.name}</span>
                    )}
                </div>

                {routesLoading && routes.length === 0 ? (
                  <p className="text-sm text-slate-500">Loading routes…</p>
                ) : (
                  <RouteCards
                    routes={routes}
                    selectedId={routeParam}
                    onSelect={(r) => select({ route: r.id })}
                    onNew={() => select({ route: 'new' })}
                    canManage={canManage && laneCanChange}
                  />
                )}

                {routeParam === 'new' && canManage && laneCanChange && (
                  <RouteEditor
                    key={`new-${lane.id}`}
                    lane={lane}
                    routeId={null}
                    geofences={geofences}
                    tollPlazas={tollPlazas}
                    canManage={canManage}
                    canAdvise={canAdvise}
                    onSaved={fetchRoutes}
                    onCreated={async (route) => {
                      await Promise.all([fetchRoutes(), fetchLanes()]);
                      select({ route: route.id });
                    }}
                    onCancelNew={() => select({ route: null })}
                  />
                )}
                {routeParam && routeParam !== 'new' && (
                  <RouteEditor
                    key={routeParam}
                    lane={lane}
                    routeId={routeParam}
                    geofences={geofences}
                    tollPlazas={tollPlazas}
                    canManage={canManage}
                    canAdvise={canAdvise}
                    onSaved={() => {
                      fetchRoutes();
                      fetchLanes();
                    }}
                    onCreated={() => {}}
                    onCancelNew={() => {}}
                  />
                )}
              </>
            )}
          </div>
        </div>
      </div>

      <NewLaneDialog
        isOpen={newLaneOpen}
        sites={sites}
        onClose={() => setNewLaneOpen(false)}
        onCreate={handleCreateLane}
      />
    </div>
  );
}
